import LogRocket from 'logrocket';
import LogrocketFuzzySanitizer from 'logrocket-fuzzy-search-sanitizer';
import { UserResponse } from '../providers/User/User.model';
import { SharedTokenResponse } from '../redux/api/orders/orders.model';

export interface BasicTenant {
  id: string;
  company_name: string;
}

interface IUserTraits {
  [propName: string]: string | number | boolean;
}

// addLogRocketIdentityForSharedLink
export const addLogRocketIdentityForSharedLink = (sharedLink: SharedTokenResponse): void => {
  // If user Response exists identify user and return
  if (sharedLink.driver && sharedLink.driver.twuser_id) {
    // Basic User Info
    const userInfo: any = {
      displayName: `${sharedLink.driver.first_name} ${sharedLink.driver.last_name}`,
      email: sharedLink.driver.email_address
    };

    // If User response has tenant add that user tenantId and companyName
    if (sharedLink.tenant) {
      userInfo.tenantId = sharedLink.tenant.id;
      userInfo.companyName = sharedLink.tenant.company_name;
    }

    LogRocket.identify(sharedLink.driver.twuser_id, userInfo);
  }

  // If tenant exists identify anonymous
  else if (sharedLink.tenant) {
    identifyLogRocketForAnonymous(sharedLink.tenant);
  }
};

export const addLogRocketIdentity = (userResponse?: UserResponse, tenant?: BasicTenant): void => {
  // If user Response exists identify user and return
  if (userResponse) {
    identifyLogRocketForUser(userResponse);
    return;
  }

  // If tenant exists identify anonymous
  if (tenant) {
    identifyLogRocketForAnonymous(tenant);
  }
};

const identifyLogRocketForUser = (userResponse: UserResponse): void => {
  // Basic User Info
  const userInfo: any = {
    displayName: `${userResponse.first_name} ${userResponse.last_name}`,
    email: userResponse.email_address,
    role: userResponse.role
  };

  // If User response has tenant add that user tenantId and companyName
  if (userResponse?.tenant) {
    userInfo.tenantId = userResponse.tenant.id;
    userInfo.companyName = userResponse.tenant.company_name;
  }

  LogRocket.identify(userResponse.id, userInfo);
};

const identifyLogRocketForAnonymous = (tenant: BasicTenant): void => {
  // Basic Tenant Info
  const tenantInfo: IUserTraits = {
    tenantId: tenant.id,
    companyName: tenant.company_name
  };

  LogRocket.identify(tenantInfo);
};

// pulling out from LogrocketFuzzySanitizer to cast IRequest below
interface INetworkRequestResponse {
  body: any;
  method: string;
  headers: any;
}

// THIS value will be set by the docker-entrypoint.sh script
const releaseGitHash = '(20250704_03:07:21_44ec6c15ac21a8785dc682342e80132541d01e36)';

/** Initialized LogRocket util with request body sanitizer to prevent
 * logging sensitive data */
export const initializeLogRocket = (apiKey: string): void => {
  // fields we don't want logged in LogRocket
  const privateFieldNames = ['password'];

  // using LogRocketFuzzySanitizer plugin to sanitize network requests but still logging the body
  const fuzzySanitizer = LogrocketFuzzySanitizer.setup(privateFieldNames);
  const fsRequestSanitizer = fuzzySanitizer.requestSanitizer;

  // if releaseGitHash is not empty, use it as the release else don't set it
  const release = releaseGitHash ? releaseGitHash : undefined;

  LogRocket.init(apiKey, {
    release,
    network: {
      isEnabled: true,
      requestSanitizer: (request) => {
        // If the request is to Amplitude, we don't need to track it
        if (request.url.includes('amplitude.com')) {
          return null;
        }

        return fsRequestSanitizer(request as INetworkRequestResponse);
      }
    }
  });
};

type TrackSeverity = 'low' | 'medium' | 'high' | 'critical';
interface LogRocketTrackEventProps {
  action: string;
  severity: TrackSeverity;
  tenant_id?: string;
  company_name?: string;
  email?: string;
  user_first_name?: string;
  user_last_name?: string;
}

export const logRocketTrack = (eventName: string, eventProps?: LogRocketTrackEventProps): void => {
  const { user_first_name, user_last_name, ...restProps } = eventProps || {};
  const displayName = `${user_first_name || ''} ${user_last_name || ''}`.trim();

  LogRocket.track(eventName, { ...restProps, displayName });
};
